import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Paperclip } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { TIPI_STRUTTURA, coerceTipoStruttura } from "../../config/tipiStruttura";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = {
  nome: "",
  tipo_struttura: "RSA",
  posti_totali: "",
  telefono: "",
  comune: "",
  via: "",
  numero_civico: "",
  provincia: "",
  cap: "",
  partita_iva: "",
  referente_nome: "",
  referente_email: "",
  pec: "",
  sdi: "",
  nota: "",
};
const ATTACHMENTS = [
  { key: "contratto", label: "Contratto" },
  { key: "brochure", label: "Brochure" },
  { key: "preventivo", label: "Preventivo (facoltativo)" },
];

function errorMessage(json, fallback) {
  return typeof json?.detail === "string" ? json.detail : fallback;
}

export default function CommercialeInvitoDialog({ open, onOpenChange, lead, onCreated }) {
  const token = localStorage.getItem("super_admin_token");
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState({ contratto: null, brochure: null, preventivo: null });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFiles({ contratto: null, brochure: null, preventivo: null });
    setForm({
      ...EMPTY,
      nome: lead?.nome_struttura || "",
      tipo_struttura: coerceTipoStruttura(lead?.tipologia) || "RSA",
      posti_totali: lead?.numero_ospiti ?? "",
      telefono: lead?.telefono || "",
      comune: lead?.comune || "",
      provincia: lead?.provincia || "",
      referente_nome: lead?.referente || "",
      referente_email: lead?.email || "",
    });
  }, [open, lead]);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    if (!form.nome.trim() || !form.referente_nome.trim() || !form.referente_email.trim()) {
      toast.error("Inserisci struttura, referente e email.");
      return;
    }
    setSaving(true);
    try {
      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key === "posti_totali") {
          payload.append(key, String(value === "" || value == null ? 1 : value));
          return;
        }
        payload.append(key, value == null ? "" : String(value));
      });
      if (lead?.id) payload.append("prospect_id", lead.id);
      ATTACHMENTS.forEach(({ key }) => {
        if (files[key]) payload.append(key, files[key]);
      });
      const res = await fetch(`${API}/api/admin/commerciale/inviti`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: payload,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(json, "Invio invito non riuscito"));
      toast.success(json.messaggio || (json.invito_inviato ? "Invito inviato" : "Invito creato"));
      onOpenChange(false);
      onCreated?.(json.invito);
    } catch (error) {
      toast.error(error.message || "Errore durante l'invio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{lead ? `Invita ${lead.nome_struttura}` : "Nuovo invito onboarding"}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">
          Il referente riceve un link per completare registrazione, piano e pagamento. Puoi allegare contratto, brochure e preventivo.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Nome struttura *</Label>
            <Input value={form.nome} onChange={(event) => setField("nome", event.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Tipologia *</Label>
            <Select value={form.tipo_struttura} onValueChange={(value) => setField("tipo_struttura", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Seleziona tipo" />
              </SelectTrigger>
              <SelectContent>
                {TIPI_STRUTTURA.map((tipo) => (
                  <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {[
            ["posti_totali", "Posti / ospiti", "number"],
            ["referente_nome", "Referente *"],
            ["referente_email", "Email referente *", "email"],
            ["telefono", "Telefono", "tel"],
            ["via", "Via"],
            ["numero_civico", "N. civico"],
            ["cap", "CAP"],
            ["comune", "Comune"],
            ["provincia", "Provincia"],
            ["partita_iva", "Partita IVA"],
            ["pec", "PEC"],
            ["sdi", "Codice SDI"],
          ].map(([key, label, type]) => (
            <div key={key}>
              <Label className="text-xs">{label}</Label>
              <Input type={type || "text"} value={form[key]} onChange={(event) => setField(key, event.target.value)} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <Label className="text-xs">Nota per il team (non inviata al referente)</Label>
            <Textarea rows={2} value={form.nota} onChange={(event) => setField("nota", event.target.value)} />
          </div>
          <div className="sm:col-span-2 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <Paperclip className="h-3.5 w-3.5" /> Allegati in email
            </p>
            <div className="grid gap-2 sm:grid-cols-3">
              {ATTACHMENTS.map(({ key, label }) => (
                <div key={key}>
                  <Label className="text-xs">{label}</Label>
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf"
                    onChange={(event) => setFiles((current) => ({ ...current, [key]: event.target.files?.[0] || null }))}
                  />
                  {files[key] ? <p className="mt-1 truncate text-[11px] text-slate-500">{files[key].name}</p> : null}
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Crea e invia invito
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
